import type { ReactNode } from 'react'

import Link from 'next/link'
import { cn } from '@/lib/utils'

export interface BarListItem {
    key: string
    label: ReactNode
    value: number
    display: ReactNode
    href?: string
    sublabel?: ReactNode
}

interface BarListProps {
    items: BarListItem[]
    diverging?: boolean
    color?: string
    className?: string
    labelWidth?: string
}

const BarList = ({ items, diverging = false, color = 'var(--series-1)', className, labelWidth = 'w-40' }: BarListProps): ReactNode => {
    const maxAbs = Math.max(1e-9, ...items.map(item => Math.abs(item.value)))

    return (
        <ul className={cn('space-y-1', className)}>
            {items.map(item => {
                const share = Math.min(100, (Math.abs(item.value) / maxAbs) * 100)
                const positive = item.value >= 0
                const fill = diverging ? (positive ? 'var(--up)' : 'var(--down)') : color

                const content = (
                    <div className='flex items-center gap-3 rounded-md px-2 py-1.5 transition-colors hover:bg-secondary/60'>
                        <div className={cn('min-w-0 shrink-0 truncate text-[13px] text-foreground', labelWidth)}>
                            {item.label}
                            {item.sublabel && <span className='block truncate text-[11px] text-muted-foreground'>{item.sublabel}</span>}
                        </div>

                        <div className='relative h-2.5 min-w-0 flex-1' aria-hidden='true'>
                            {diverging ? (
                                <>
                                    <span className='absolute inset-y-0 left-1/2 w-px bg-axis' />
                                    <span
                                        className={cn('absolute inset-y-0 rounded-[3px]', positive ? 'left-1/2' : 'right-1/2')}
                                        style={{ width: `${share / 2}%`, background: fill }}
                                    />
                                </>
                            ) : (
                                <>
                                    <span className='absolute inset-0 rounded-[3px] bg-secondary' />
                                    <span className='absolute inset-y-0 left-0 rounded-[3px]' style={{ width: `${Math.max(1.5, share)}%`, background: fill }} />
                                </>
                            )}
                        </div>

                        <div className='w-20 shrink-0 text-right text-[13px] font-medium tabular-nums text-foreground'>
                            {item.display}
                        </div>
                    </div>
                )

                return (
                    <li key={item.key}>
                        {item.href
                            ? <Link href={item.href} className='block rounded-md focus-visible:outline-2'>{content}</Link>
                            : content}
                    </li>
                )
            })}
        </ul>
    )
}

export default BarList
